/**
 * Centralized semantic icon aliases for Koob UI.
 * Maps semantic intention to full Font Awesome Pro 6 class names.
 */
export const icons = {
  // Actions
  add: 'fa-regular fa-plus',
  edit: 'fa-regular fa-pen',
  delete: 'fa-regular fa-trash-can',
  view: 'fa-regular fa-eye',
  viewOff: 'fa-regular fa-eye-slash',
  duplicate: 'fa-regular fa-copy',
  copy: 'fa-regular fa-clone',
  archive: 'fa-regular fa-box-archive',
  restore: 'fa-regular fa-arrows-rotate',
  download: 'fa-regular fa-arrow-down-to-line',
  arrowUp: 'fa-regular fa-arrow-up',
  upload: 'fa-regular fa-arrow-up-from-bracket',
  search: 'fa-regular fa-magnifying-glass',
  filter: 'fa-regular fa-filter',
  link: 'fa-regular fa-link',
  externalLink: 'fa-regular fa-arrow-up-right-from-square',
  send: 'fa-regular fa-paper-plane',
  refresh: 'fa-regular fa-rotate-right',
  close: 'fa-regular fa-xmark',
  check: 'fa-regular fa-check',
  minus: 'fa-regular fa-minus',
  more: 'fa-regular fa-ellipsis',
  moreVertical: 'fa-regular fa-ellipsis-vertical',
  drag: 'fa-regular fa-grip-vertical',
  settings: 'fa-regular fa-gear',
  lock: 'fa-regular fa-lock',
  history: 'fa-regular fa-clock-rotate-left',

  // Navigation & Menus
  chevronUp: 'fa-regular fa-chevron-up',
  chevronDown: 'fa-regular fa-chevron-down',
  chevronLeft: 'fa-regular fa-chevron-left',
  chevronRight: 'fa-regular fa-chevron-right',
  arrowLeft: 'fa-regular fa-arrow-left',
  arrowRight: 'fa-regular fa-arrow-right',
  expand: 'fa-regular fa-expand',
  collapse: 'fa-regular fa-compress',
  list: 'fa-regular fa-list',
  map: 'fa-regular fa-map',
  grid: 'fa-regular fa-table-cells',

  // Main Sidebar & Modules (Koob Navigation)
  organizations: 'fa-regular fa-building-magnifying-glass',
  myOrganization: 'fa-regular fa-house-building',
  bookings: 'fa-regular fa-ticket',
  hotels: 'fa-regular fa-bed',
  experiences: 'fa-regular fa-layer-group',
  tripDesigner: 'fa-regular fa-cube',
  locations: 'fa-regular fa-globe',
  travelersSettings: 'fa-regular fa-briefcase',
  travelDiary: 'fa-regular fa-book',
  travelDiaryContent: 'fa-regular fa-book-open',
  distribution: 'fa-regular fa-link',
  allotments: 'fa-regular fa-bolt',
  nightRates: 'fa-regular fa-moon',
  supportCenter: 'fa-regular fa-file-lines',
  support: 'fa-regular fa-headset',
  users: 'fa-regular fa-circle-user',
  requestAccess: 'fa-regular fa-user-check',
  integrations: 'fa-regular fa-puzzle-piece',
  apiKeys: 'fa-regular fa-key',

  // Status & Indicators
  info: 'fa-regular fa-circle-info',
  success: 'fa-regular fa-circle-check',
  warning: 'fa-regular fa-triangle-exclamation',
  error: 'fa-regular fa-circle-xmark',
  pending: 'fa-regular fa-clock',
  star: 'fa-regular fa-star',
  starFilled: 'fa-solid fa-star',
  flag: 'fa-regular fa-flag',
  dot: 'fa-solid fa-circle',
  sparkles: 'fa-regular fa-sparkles',

  // Domain & Entities
  hotel: 'fa-regular fa-hotel',
  bed: 'fa-regular fa-bed',
  roomCategory: 'fa-regular fa-door-open',
  experience: 'fa-regular fa-compass',
  location: 'fa-regular fa-location-dot',
  calendar: 'fa-regular fa-calendar',
  clock: 'fa-regular fa-clock',
  duration: 'fa-regular fa-hourglass-half',
  night: 'fa-regular fa-moon',
  traveler: 'fa-regular fa-user',
  travelers: 'fa-regular fa-users',
  child: 'fa-regular fa-child',
  baby: 'fa-regular fa-baby',
  agency: 'fa-regular fa-building',
  organization: 'fa-regular fa-sitemap',
  price: 'fa-regular fa-tag',
  deal: 'fa-regular fa-handshake',
  booking: 'fa-regular fa-calendar-check',
  trip: 'fa-regular fa-route',
  message: 'fa-regular fa-comment',
  email: 'fa-regular fa-envelope',
  phone: 'fa-regular fa-phone',
  language: 'fa-regular fa-globe',
  notification: 'fa-regular fa-bell',
  cart: 'fa-regular fa-cart-shopping',
  logout: 'fa-regular fa-right-from-bracket',
  profile: 'fa-regular fa-id-badge',
  help: 'fa-regular fa-circle-question',
  theme: 'fa-regular fa-circle-half-stroke',
  docs: 'fa-regular fa-file-lines',
  impersonationStop: 'fa-regular fa-user-slash',
  thumbsUp: 'fa-regular fa-thumbs-up',
  thumbsDown: 'fa-regular fa-thumbs-down',
  size: 'fa-regular fa-ruler-combined',
  newChat: 'fa-regular fa-pen-to-square',
} as const;

export type KoobIconName = keyof typeof icons;

export const iconGroups: Record<string, readonly KoobIconName[]> = {
  'Sidebar & Navigation': [
    'organizations',
    'myOrganization',
    'bookings',
    'hotels',
    'experiences',
    'tripDesigner',
    'locations',
    'travelersSettings',
    'travelDiary',
    'travelDiaryContent',
    'distribution',
    'allotments',
    'nightRates',
    'supportCenter',
    'support',
    'users',
    'requestAccess',
    'integrations',
    'apiKeys',
  ],
  Actions: [
    'add',
    'edit',
    'delete',
    'view',
    'viewOff',
    'duplicate',
    'copy',
    'archive',
    'restore',
    'download',
    'upload',
    'search',
    'filter',
    'link',
    'externalLink',
    'send',
    'refresh',
    'close',
    'check',
    'minus',
    'more',
    'moreVertical',
    'drag',
    'settings',
    'lock',
    'history',
    'thumbsUp',
    'thumbsDown',
  ],
  Navigation: [
    'chevronUp',
    'chevronDown',
    'chevronLeft',
    'chevronRight',
    'arrowLeft',
    'arrowRight',
    'expand',
    'collapse',
    'list',
    'map',
    'grid',
  ],
  Status: [
    'info',
    'success',
    'warning',
    'error',
    'pending',
    'star',
    'starFilled',
    'flag',
    'dot',
    'sparkles',
  ],
  'Koob Domain': [
    'hotel',
    'bed',
    'roomCategory',
    'experience',
    'location',
    'calendar',
    'clock',
    'duration',
    'night',
    'traveler',
    'travelers',
    'child',
    'baby',
    'agency',
    'organization',
    'price',
    'deal',
    'booking',
    'trip',
    'message',
    'email',
    'phone',
    'language',
    'notification',
    'cart',
    'logout',
    'profile',
    'help',
    'theme',
    'docs',
    'impersonationStop',
  ],
};
